import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { t } from '@/i18n/ro';
import { Button, Input, Card } from '@/components/common';
import type { AuthCredentials } from '@/types';
import type { ApiError } from '@/services/api';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState<AuthCredentials>({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(credentials);
      navigate('/');
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.status === 401) {
        setError(t('auth.errors.invalidCredentials'));
      } else if (apiError.message) {
        setError(apiError.message);
      } else {
        setError(t('auth.errors.connectionFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof AuthCredentials, value: string) => {
    setCredentials((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo și titlu */}
        <div className="text-center mb-8">
          <img
            src="/logo-login.png"
            alt="Consiliul Județean Prahova"
            className="h-24 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900">
            Managementul Proiectelor
          </h1>
          <p className="text-gray-600 mt-2">Consiliul Județean Prahova</p>
        </div>

        {/* Form */}
        <Card className="shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error message */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-danger-50 border border-danger-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-danger-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-danger-700">{error}</p>
              </div>
            )}

            {/* Username */}
            <Input
              label={t('auth.username')}
              type="text"
              placeholder={t('auth.usernamePlaceholder')}
              value={credentials.username}
              onChange={(e) => handleChange('username', e.target.value)}
              leftIcon={<User className="w-5 h-5" />}
              required
              autoComplete="username"
            />

            {/* Password */}
            <Input
              label={t('auth.password')}
              type="password"
              placeholder={t('auth.passwordPlaceholder')}
              value={credentials.password}
              onChange={(e) => handleChange('password', e.target.value)}
              leftIcon={<Lock className="w-5 h-5" />}
              required
              autoComplete="current-password"
            />

            {/* Submit button */}
            <Button
              type="submit"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              {isLoading ? t('auth.loggingIn') : t('auth.login')}
            </Button>
          </form>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Platformă de management al proiectelor
        </p>
      </div>
    </div>
  );
}
